/**
 * Reseller Economics Section
 * Master Admin can edit, Sub-Master can only view
 * 
 * Shows:
 * - Reseller Cost (monthly)
 * - Reseller Retail (monthly)
 * - Calculated Monthly Margin
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "—";
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const ResellerEconomicsSection = ({ dealerId, readOnly = false }) => {
  const { apiFetch, user } = useAuth();
  const { success: showToast, error: showError } = useToast();
  
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ cost: "", retail: "" });
  
  const isMasterAdmin = user?.role === "MASTER_ADMIN";
  const canEdit = isMasterAdmin && !readOnly;
  
  // Fetch assignments for this dealer
  const fetchAssignments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`/submaster/assignments?business_id=${dealerId}`);
      setAssignments(response.data.assignments || []);
    } catch (err) {
      console.error("Failed to fetch reseller assignments:", err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, dealerId]);
  
  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);
  
  const handleEdit = (assignment) => {
    setEditingId(assignment.id);
    setEditValues({
      cost: assignment.reseller_cost || "",
      retail: assignment.reseller_retail || "",
    });
  };
  
  const handleCancel = () => {
    setEditingId(null);
    setEditValues({ cost: "", retail: "" });
  };
  
  const handleSave = async (assignmentId) => {
    try {
      setSaving(true);
      await apiFetch(`/submaster/assignments/${assignmentId}/pricing`, {
        method: "PUT",
        data: {
          reseller_cost: editValues.cost ? parseFloat(editValues.cost) : null,
          reseller_retail: editValues.retail ? parseFloat(editValues.retail) : null,
        },
      });
      showToast("Reseller pricing updated");
      setEditingId(null);
      fetchAssignments();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to update pricing");
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Reseller Economics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse h-20 bg-gray-100 rounded" />
        </CardContent>
      </Card>
    );
  }
  
  if (assignments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Reseller Economics</CardTitle>
          <CardDescription>No resellers assigned to this dealer</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">
            Assign this dealer to a reseller (Sub-Master) to enable margin tracking.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card data-testid="reseller-economics-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Reseller Economics</CardTitle>
            <CardDescription>Monthly pricing for reseller assignments</CardDescription>
          </div>
          {!canEdit && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="secondary" className="text-xs">
                    View Only
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Pricing managed by Verified Demand</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {assignments.map((assignment) => {
            const isEditing = editingId === assignment.id;
            const cost = assignment.reseller_cost || 0;
            const retail = assignment.reseller_retail || 0;
            const margin = retail - cost;
            
            return (
              <div 
                key={assignment.id} 
                className="border rounded-lg p-4 bg-gray-50"
                data-testid={`reseller-assignment-${assignment.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="font-medium text-gray-900">
                      {assignment.sub_master_name || assignment.sub_master_email}
                    </span>
                    <p className="text-sm text-gray-500">{assignment.sub_master_email}</p>
                  </div>
                  {canEdit && !isEditing && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEdit(assignment)}
                      data-testid={`edit-pricing-${assignment.id}`}
                    >
                      Edit Pricing
                    </Button>
                  )}
                </div>
                
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Cost (Monthly)</label>
                        <div className="mt-1 relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <Input
                            type="number"
                            value={editValues.cost}
                            onChange={(e) => setEditValues(prev => ({ ...prev, cost: e.target.value }))}
                            className="pl-7"
                            placeholder="0"
                            data-testid="reseller-cost-input"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Retail (Monthly)</label>
                        <div className="mt-1 relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <Input
                            type="number"
                            value={editValues.retail}
                            onChange={(e) => setEditValues(prev => ({ ...prev, retail: e.target.value }))}
                            className="pl-7"
                            placeholder="0"
                            data-testid="reseller-retail-input"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleSave(assignment.id)}
                        disabled={saving}
                        data-testid="save-pricing-btn"
                      >
                        {saving ? "Saving..." : "Save"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Cost</p>
                      <p className="text-lg font-semibold text-gray-700">{formatCurrency(cost)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Retail</p>
                      <p className="text-lg font-semibold text-gray-700">{formatCurrency(retail)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Margin</p>
                      <p className={`text-lg font-semibold ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(margin)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ResellerEconomicsSection;
